import { z } from 'zod'
import { digitsOnly, usPhoneNumber } from 'lib/phone'

/**
 * The quick-create heading takes a name or a phone number, because the front desk
 * often starts from caller ID and wants to know who it is before typing anything
 * else. A complete, letterless US number is a phone; anything else, including a
 * partial number, stays a name.
 */
export function phoneFromHeading(text: string): string | undefined {
  return /[a-z]/i.test(text) ? undefined : usPhoneNumber(text)
}

export type IdentityProblem = { field: `name` | `cellPhone`; message: string }

/**
 * A contact needs a name and a way to reach them (phone or email). A number typed
 * as the heading finds the caller but never stands in for their name: the record
 * is not created until someone types one (Patrick, 2026-09-04).
 */
export function identityProblem({
  name,
  cellPhone,
  email,
}: {
  name: string
  cellPhone: string
  email: string
}): IdentityProblem | undefined {
  if (!name.trim()) return { field: `name`, message: `Enter a name` }
  if (!cellPhone && !email) return { field: `cellPhone`, message: `Enter a cell phone or an email` }
  return undefined
}

/**
 * A number typed as the heading belongs in the phone field. Runs on Tab so the
 * number visibly moves down and the heading is free for the name, and before
 * validation so a number left in the heading is judged as a phone, not a name.
 * A phone typed separately wins.
 */
export function resolveHeading({ name, cellPhone }: { name: string; cellPhone: string }): {
  name: string
  cellPhone: string
} {
  const headingPhone = phoneFromHeading(name)
  if (!headingPhone || cellPhone) return { name, cellPhone }
  return { name: ``, cellPhone: headingPhone }
}

type LocationOption = { id?: string; name?: string }

/**
 * Which locations the modal offers and whether a choice is even needed. A
 * workspace with one location, or a user allowed into only one, never sees the
 * location line. Access ids that match nothing (stale after a location was
 * removed) fall back to the whole workspace rather than an empty picker.
 */
export function locationChoice<T extends LocationOption>(
  locations: T[],
  accessibleIds: string[] | undefined,
  defaultLocationId: string | undefined,
): { options: T[]; preselectedId: string | undefined } {
  const accessible = accessibleIds?.length ? locations.filter((l) => l.id && accessibleIds.includes(l.id)) : []
  const options = accessible.length ? accessible : locations
  const preselectedId = options.length === 1 ? options[0]?.id : options.find((l) => l.id === defaultLocationId)?.id
  return { options, preselectedId }
}

/** Avatar text for a contact: first letter of the first two words. */
export function initials(name?: string): string {
  return (name ?? ``)
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? ``)
    .join(``)
}

/**
 * The quick-create form. Every requirement is checked in one refinement pass on
 * purpose: a failure in the base shape would skip the transform and the identity
 * checks, and "Pick a location" would be reported before "Enter a name".
 */
export const quickCreateSchema = z
  .object({
    name: z.string().trim(),
    cellPhone: z.string().transform(digitsOnly),
    email: z.string().trim(),
    locationId: z.string(),
    becameLeadDate: z.date(),
    leadStatus: z.string().min(1),
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
    channel: z.string().optional(),
    leadOwner: z.object({ id: z.string(), name: z.string() }).optional(),
  })
  // A number typed as the heading is judged as a phone, not a name, so Enter
  // asks for the name instead of saving a contact called "2075550142".
  .transform((values) => ({ ...values, ...resolveHeading(values) }))
  .superRefine((data, ctx) => {
    const problem = identityProblem(data)
    if (problem) ctx.addIssue({ code: `custom`, path: [problem.field], message: problem.message })
    if (!data.locationId) ctx.addIssue({ code: `custom`, path: [`locationId`], message: `Pick a location` })
    if (data.cellPhone && data.cellPhone.length !== 10) {
      ctx.addIssue({ code: `custom`, path: [`cellPhone`], message: `Cell phone must be 10 digits` })
    }
    if (data.email && !data.email.includes(`@`)) {
      ctx.addIssue({ code: `custom`, path: [`email`], message: `Email must be valid` })
    }
  })

export type QuickCreateValues = z.input<typeof quickCreateSchema>
export type QuickCreateOutput = z.output<typeof quickCreateSchema>

/** The order the fields are filled, so a missing name is asked for before a missing location. */
const FILL_ORDER: (keyof QuickCreateValues)[] = [`name`, `cellPhone`, `email`, `locationId`]

export function firstProblemField(
  errors: Partial<Record<keyof QuickCreateValues, unknown>>,
): keyof QuickCreateValues | undefined {
  return FILL_ORDER.find((field) => errors[field]) ?? (Object.keys(errors) as (keyof QuickCreateValues)[])[0]
}
